import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MoveRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const ArtistsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const artists = useAdminStore((s) => s.artists).filter((a) => a.isActive !== false);
  const allArtworks = useAdminStore((s) => s.artworks);

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-20"
        >
          <h1 className="text-3xl sm:text-5xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {t.artists.title}
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          {artists.map((artist, i) => (
            <motion.div
              key={artist._id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15, duration: 0.8 }}
              className="group cursor-pointer"
              onClick={() => navigate(`/artists/${artist.slug}`)}
            >
              <div className="relative aspect-[3/4] overflow-hidden rounded-2xl mb-6 bg-[#f9f9f9] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5">
                {artist.portrait ? (
                  <img
                    src={artist.portrait}
                    alt={localize(artist.name)}
                    className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-black/10 dark:text-white/10">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />
                <div className="absolute bottom-6 left-6 right-6 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center bg-white/10 backdrop-blur-md">
                      <MoveRight size={16} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest">
                      {t.artists.seeMore}
                    </span>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter">
                {localize(artist.name)}
              </h3>
              <p className="text-sm text-black/40 dark:text-white/40 mt-2 line-clamp-2">
                {localize(artist.bio)}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-black/20 dark:text-white/20 mt-3">
                {allArtworks.filter((w) => typeof w.artist !== 'string' && w.artist._id === artist._id).length} {t.artists.works}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ArtistsPage;
